import { ReactElement } from 'react';
import { createUnifiedTheme, UnifiedThemeProvider } from '@backstage/theme';
import { colorsLight as c } from '../tokens/colorsLight';
import { fontFamilyCorpoSText } from '../tokens/typography';

export const odpV2LightTheme = createUnifiedTheme({
  palette: {
    mode: 'light',
    background: {
      default: c.background.default,
      paper: c.background.paper,
    },
    primary: {
      main: c.primary.main,
    },
    secondary: {
      main: c.primary.main,
    },
    text: {
      primary: c.text.primary,
      secondary: c.text.secondary,
    },
    divider: c.border.default,
    status: {
      ok: '#1DB954',
      warning: '#FF9800',
      error: '#E22134',
      running: '#1F5493',
      pending: '#FFED51',
      aborted: '#757575',
    },
    error: {
      main: c.status.critical,
    },
    warning: {
      main: c.status.degraded,
    },
    success: {
      main: c.status.healthy,
    },
    // Backstage palette extensions
    textVerySubtle: '#9E9E9E',
    textSubtle: '#616161',
    textContrast: '#1A1A1A',
    highlight: '#E3F2FD',
    errorBackground: '#FFEBEE',
    warningBackground: '#FFF3E0',
    infoBackground: '#E3F2FD',
    errorText: '#C62828',
    infoText: '#1565C0',
    warningText: '#E65100',
    linkHover: '#004C8C',
    link: '#0066BB',
    gold: '#FFB300',
    banner: {
      info: '#2E77D0',
      error: '#E22134',
      text: '#FFFFFF',
      link: '#FFFFFF',
      closeButtonColor: '#FFFFFF',
      warning: '#FF9800',
    },
    navigation: {
      background: c.background.sidebar,
      indicator: c.primary.main,
      color: c.sidebar.inactiveText,
      selectedColor: c.sidebar.activeText,
      navItem: {
        hoverBackground: c.sidebar.hover,
      },
    },
  } as any,
  fontFamily: fontFamilyCorpoSText,
  typography: {
    fontFamily: fontFamilyCorpoSText,
    h1: { fontFamily: fontFamilyCorpoSText },
    h2: { fontFamily: fontFamilyCorpoSText },
    h3: { fontFamily: fontFamilyCorpoSText },
    h4: { fontFamily: fontFamilyCorpoSText },
    h5: { fontFamily: fontFamilyCorpoSText },
    h6: { fontFamily: fontFamilyCorpoSText },
    subtitle1: { fontFamily: fontFamilyCorpoSText },
    subtitle2: { fontFamily: fontFamilyCorpoSText },
    body1: { fontFamily: fontFamilyCorpoSText },
    body2: { fontFamily: fontFamilyCorpoSText },
    button: { fontFamily: fontFamilyCorpoSText },
    caption: { fontFamily: fontFamilyCorpoSText },
    overline: { fontFamily: fontFamilyCorpoSText },
  } as any,
  components: {
    MuiTypography: {
      styleOverrides: {
        root: {
          color: c.text.primary,
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: c.background.paper,
          backgroundImage: 'none',
          boxShadow: 'none',
          borderBottom: `1px solid ${c.border.default}`,
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: c.background.sidebar,
          borderRight: `1px solid ${c.border.default}`,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backgroundColor: c.background.paper,
          borderColor: c.border.default,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: c.background.paper,
          backgroundImage: 'none',
          border: `1px solid ${c.border.default}`,
          borderRadius: '12px',
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          color: c.text.primary,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        containedPrimary: {
          backgroundColor: c.primary.main,
          color: '#FFFFFF',
          '&:hover': {
            backgroundColor: '#006ABD',
          },
        },
        outlinedPrimary: {
          borderColor: c.primary.main,
          color: c.primary.main,
          '&:hover': {
            borderColor: '#004C8C',
            backgroundColor: 'rgba(0, 120, 214, 0.06)',
          },
        },
        text: {
          color: 'rgba(0, 0, 0, 0.65)',
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          borderBottom: `1px solid ${c.border.default}`,
        },
        indicator: {
          backgroundColor: c.primary.main,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          color: c.text.secondary,
          '&.Mui-selected': {
            color: c.text.primary,
          },
          '&:hover': {
            color: 'rgba(0, 0, 0, 0.8)',
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
          },
        },
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: {
          backgroundColor: c.background.paper,
          borderRadius: '12px',
          border: `1px solid ${c.border.default}`,
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-head': {
            backgroundColor: c.background.elevated,
            color: c.text.secondary,
            fontWeight: 600,
            borderBottom: `1px solid ${c.border.default}`,
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          color: c.text.primary,
          borderBottom: `1px solid ${c.border.default}`,
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.02)',
          },
        },
      },
    },
    MuiTablePagination: {
      styleOverrides: {
        root: {
          color: c.text.secondary,
        },
        selectIcon: {
          color: c.text.secondary,
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          backgroundColor: c.background.paper,
          color: c.text.primary,
          borderRadius: '8px',
        },
        input: {
          color: c.text.primary,
          '&::placeholder': {
            color: c.text.secondary,
            opacity: 1,
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: c.text.secondary,
          '&.Mui-focused': {
            color: c.primary.main,
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: c.border.default,
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(0, 0, 0, 0.3)',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: c.primary.main,
          },
        },
        notchedOutline: {
          borderColor: c.border.default,
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        icon: {
          color: c.text.secondary,
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          backgroundColor: c.background.paper,
          border: `1px solid ${c.border.default}`,
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          color: c.text.primary,
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
          },
          '&.Mui-selected': {
            backgroundColor: 'rgba(0, 120, 214, 0.1)',
            '&:hover': {
              backgroundColor: 'rgba(0, 120, 214, 0.16)',
            },
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          backgroundColor: c.background.elevated,
          color: c.text.primary,
          borderColor: c.border.default,
        },
        outlined: {
          borderColor: c.border.default,
        },
        colorPrimary: {
          backgroundColor: c.primary.main,
          color: '#ffffff',
          borderColor: c.primary.main,
        },
        filledPrimary: {
          backgroundColor: c.primary.main,
          color: '#ffffff',
          '&:hover': {
            backgroundColor: c.primary.hover,
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          backgroundColor: c.background.paper,
          backgroundImage: 'none',
          border: `1px solid ${c.border.default}`,
        },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          backgroundColor: c.background.paper,
          backgroundImage: 'none',
          borderRadius: '8px',
          border: `1px solid ${c.border.default}`,
          '&:before': {
            display: 'none',
          },
          '&.Mui-expanded': {
            margin: '8px 0',
          },
        },
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          color: c.text.primary,
        },
      },
    },
    MuiAccordionDetails: {
      styleOverrides: {
        root: {
          color: c.text.secondary,
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: '#333333',
          color: '#FFFFFF',
          border: 'none',
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        track: {
          backgroundColor: c.border.default,
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          backgroundColor: c.border.default,
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: c.border.default,
        },
      },
    },
    MuiBreadcrumbs: {
      styleOverrides: {
        separator: {
          color: c.text.secondary,
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          color: c.text.link,
        },
      },
    },
    // --- Backstage component overrides ---
    BackstageHeader: {
      styleOverrides: {
        header: {
          backgroundColor: 'transparent',
          backgroundImage: 'none',
          boxShadow: 'none',
          borderBottom: 'none',
          color: c.text.primary,
        },
        title: {
          color: c.text.primary,
        },
        subtitle: {
          color: c.text.secondary,
        },
      },
    },
    BackstageTable: {
      styleOverrides: {
        root: {
          '& .MuiToolbar-root': {
            backgroundColor: c.background.paper,
            color: c.text.primary,
          },
        },
      },
    },
    BackstageHeaderTabs: {
      styleOverrides: {
        tabsWrapper: {
          backgroundColor: 'transparent',
        },
      },
    },
    BackstageContent: {
      styleOverrides: {
        root: {
          backgroundColor: c.background.default,
        },
      },
    },
    BackstagePage: {
      styleOverrides: {
        root: {
          backgroundColor: c.background.default,
        },
      },
    },
    BackstageFavoriteToggleIcon: {
      styleOverrides: {
        icon: { display: 'none' },
        iconBorder: { display: 'none' },
      },
    },
  },
});

const LightProvider = ({ children }: { children: ReactElement }) => (
  <UnifiedThemeProvider theme={odpV2LightTheme}>
    {children}
  </UnifiedThemeProvider>
);

export const odpV2LightThemeProvider = {
  id: 'odp-v2-light',
  title: 'ODP Light (New)',
  variant: 'light' as const,
  Provider: LightProvider,
};
